import { useEffect, useRef, useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import { Swiper } from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { NavigationOptions } from 'swiper/types';
import 'swiper/css';
import 'swiper/css/pagination';
import { CoreLinkButton } from '@components/CoreLinkButton';
import { CoreBox, CoreTypography } from '@youscience/core';
import { EmployerCard, Major, School, SocCodeInfo } from '@common-types';

import { trackEvent } from '@utils/googleAnalytics';

import { SchoolCard } from '@components/SchoolCard';
import { MajorCard } from '@components/MajorCard';
import { MajorCardSkeleton } from '@components/MajorCard/MajorCardSkeleton';
import { CareerCard } from '@components/CareerCard';
import { ReducedCardSkeleton } from '@components/ReducedSchoolCard/ReducedCardSkeleton';
import { CareerCardSkeleton } from '@components/CareerCard/CareerCardSkeleton';
import { EmployerCard as EmployerCardComponent } from '@components/EmployerCard';
import { EmployerCardSkeleton } from '@components/EmployerCard/EmployerCardSkeleton';
import ActionButton from '@components/ActionButton';

import { OPPORTUNITIES_URL } from '@constants/externalRoutes';

import { BreadcrumbsLinks } from '@interfaces/location';

import ChevronLeftIcon from '@images/chevron-left.svg?react';

import { sxStyles, StyledSwiper, StyledHeader, StyledPaginationButtonsWrap } from './CardSlider.styles';

const SWIPER_DATA = {
  PAGINATION: {
    clickable: true,
  },
  BREAKPOINTS: {
    1180: {
      slidesPerView: 4,
      slidesPerGroup: 4,
    },
    1024: {
      slidesPerView: 3.5,
      slidesPerGroup: 3,
    },
    768: {
      slidesPerView: 2.4,
      slidesPerGroup: 2,
    },
    612: {
      slidesPerView: 2,
      slidesPerGroup: 2,
    },
    375: {
      slidesPerView: 1,
      slidesPerGroup: 1,
    },
  },
};

const CARD_SKELETONS: { [key: string]: JSX.Element } = {
  school: <ReducedCardSkeleton />,
  major: <MajorCardSkeleton />,
  majorWithImage: <MajorCardSkeleton isShowImage />,
  career: <CareerCardSkeleton />,
  employer: <EmployerCardSkeleton />,
};

const SLIDES_PER_VIEW = 4;

interface CardSliderProps {
  cardType: CardType;
  title: string;
  cardList: Major[] | School[] | SocCodeInfo[] | EmployerCard[];
  navigateLink?: string;
  cameFrom?: { from: BreadcrumbsLinks[] };
  buttonTitle?: string;
  onButtonClick?: () => void;
  isDataLoading?: boolean;
  dataTestId?: string;
}

export enum CardType {
  SCHOOL = 'school',
  MAJOR = 'major',
  MAJOR_WITH_IMAGE = 'majorWithImage',
  CAREER = 'career',
  EMPLOYER = 'employer',
}

export const CardSlider = (props: CardSliderProps) => {
  const {
    cardType,
    title,
    cardList,
    navigateLink = '',
    cameFrom,
    buttonTitle,
    onButtonClick,
    isDataLoading = false,
    dataTestId,
  } = props;

  const [activeIndex, setActiveIndex] = useState(0);
  const [slidesTracked, setSlidesTracked] = useState<number[]>([]);
  const slidesLength = 4;

  const navigationPrevRef = useRef<HTMLDivElement>(null);
  const navigationNextRef = useRef<HTMLDivElement>(null);

  const cardSkeletonList = Array.from({ length: 4 }, (el, index) => (
    <SwiperSlide key={index}>{CARD_SKELETONS[cardType]}</SwiperSlide>
  ));

  const handleBeforeInit = (swiper: Swiper) => {
    // eslint-disable-next-line no-param-reassign
    swiper.params.navigation = {
      ...(swiper.params.navigation as NavigationOptions),
      prevEl: navigationPrevRef.current,
      nextEl: navigationNextRef.current,
    };
  };

  const cards: { [key in CardType]: JSX.Element[] } = {
    school: (cardList as School[])?.map((card) => (
      <SwiperSlide key={card.id}>
        <SchoolCard school={card} schoolCardType="reduced" componentId="card-slider-school" />
      </SwiperSlide>
    )),
    major: (cardList as Major[])?.map((card) => (
      <SwiperSlide key={card.cipCode}>
        <MajorCard major={card} componentId="card-slider-major" />
      </SwiperSlide>
    )),
    majorWithImage: (cardList as Major[])?.map((card) => (
      <SwiperSlide key={card.cipCode}>
        <MajorCard major={card} componentId="card-slider-major" isShowImage />
      </SwiperSlide>
    )),
    career: (cardList as SocCodeInfo[])?.map((card) => (
      <SwiperSlide key={card.socCode}>
        <CareerCard career={card} componentId="card-slider-career" />
      </SwiperSlide>
    )),
    employer: (cardList as EmployerCard[])?.map((card) => (
      <SwiperSlide key={card.id}>
        <EmployerCardComponent employer={card} employerUrl={`${OPPORTUNITIES_URL}/${card.id}`} />
      </SwiperSlide>
    )),
  };

  useEffect(() => {
    if (cardType === CardType.EMPLOYER && cardList?.length > 0) {
      const employerCards = cardList as EmployerCard[];

      for (let i = activeIndex; i < activeIndex + slidesLength; i++) {
        if (i < employerCards.length && !slidesTracked.includes(i)) {
          setSlidesTracked((prev) => [...prev, i]);

          console.log('EmployerLogoViewed', {
            employerId: employerCards[i].id,
            employerName: employerCards[i].name,
          });

          trackEvent({
            eventType: 'EmployerLogoViewed',
            props: {
              employerId: employerCards[i].id,
              employerName: employerCards[i].name,
            },
          });
        }
      }
    }
  }, [cardType, activeIndex, cardList, slidesTracked]);

  if (!isDataLoading && !cards[cardType]?.length) return null;

  return (
    <CoreBox sx={sxStyles.cardSlider} data-testid={dataTestId ?? 'cardSlider'}>
      <StyledHeader data-testid="sliderHeader">
        <CoreTypography variant="h3" component="h2">
          {title}
        </CoreTypography>

        <StyledPaginationButtonsWrap>
          {buttonTitle && (
            <CoreLinkButton
              data-testid="navigation-button"
              sx={sxStyles.navigateButton}
              variant="outlined"
              to={navigateLink}
              onClick={onButtonClick}
              state={cameFrom}
              size="medium"
            >
              {buttonTitle}
            </CoreLinkButton>
          )}

          <div data-testid="left-button" ref={navigationPrevRef}>
            {cardList?.length > SLIDES_PER_VIEW && (
              <ActionButton sx={sxStyles.paginationButton}>
                <ChevronLeftIcon />
              </ActionButton>
            )}
          </div>

          <div data-testid="right-button" ref={navigationNextRef}>
            {cardList?.length > SLIDES_PER_VIEW && (
              <ActionButton sx={{ ...sxStyles.paginationButton, transform: 'rotate(180deg)' }}>
                <ChevronLeftIcon />
              </ActionButton>
            )}
          </div>
        </StyledPaginationButtonsWrap>
      </StyledHeader>

      <StyledSwiper
        watchSlidesProgress
        spaceBetween={32}
        pagination={SWIPER_DATA.PAGINATION}
        navigation={{
          nextEl: navigationNextRef.current,
          prevEl: navigationPrevRef.current,
        }}
        modules={[Pagination, Navigation]}
        breakpoints={SWIPER_DATA.BREAKPOINTS}
        onBeforeInit={handleBeforeInit}
        onSlideChange={(swiperCore) => {
          setActiveIndex(swiperCore.activeIndex);
        }}
      >
        {isDataLoading ? cardSkeletonList : cards[cardType]}
      </StyledSwiper>
    </CoreBox>
  );
};
